import c from 'classnames';
import { forwardRef, InputHTMLAttributes } from 'react';
import { IconButton } from '../Button/IconButton';
import { FormErrorMessage } from './FormErrorMessage';

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  onClear?: () => void;
  accessibleLabel: string;
  clearInputLabel?: string;
  error?: string;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (props: InputProps, ref) => {
    const {
      id,
      value,
      className = '',
      accessibleLabel,
      error,
      onChange,
      onClear,
      clearInputLabel,
      ...rest
    } = props;
    return (
      <div className={className}>
        <label htmlFor={id} className="mb-1 font-semibold text-white">
          {accessibleLabel}
        </label>
        <div className="relative">
          <input
            {...rest}
            value={value}
            ref={ref}
            aria-invalid={Boolean(error)}
            id={id}
            aria-describedby={error ? `${id}-error` : undefined}
            className={c(
              'body-small focus-outline w-full rounded-lg border border-grey-400 bg-transparent p-3 pr-10 text-white placeholder:font-medium placeholder:text-grey-400 focus:border-transparent',
              {
                ['border-0 outline outline-2 outline-red-500']: Boolean(error),
              }
            )}
            onChange={onChange}
          />
          <div className="absolute right-4 top-3 mt-[2px] flex items-center gap-1">
            {value && onClear && (
              <IconButton
                iconName="X"
                ariaLabel={clearInputLabel || 'Clear input'}
                iconClassName="hover:fill-[#4268E6]"
                onClick={onClear}
              />
            )}
          </div>
        </div>
        <div aria-live="polite">
          {error && <FormErrorMessage inputId={id} errorMessage={error} />}
        </div>
      </div>
    );
  }
);

Input.displayName = 'Input';
